import { AIBlockConfig } from "../ai-block-types";
import { TemplateData } from "../templates/utils";
import consts from "shared/consts";
import { NewElementData } from "shared/datamodel/canvas-element";
import { centerElementsAroundOrigin } from "utils/template-utils";
import { ModelBoardGanttChartResponse } from "shared/ai-model/responses/board-gantt-chart-schema";
import { placeGanttElement } from "shared/datamodel/gantt";
import { IntegrationType } from "shared/integrations/integration";
import { placeIntegrationElement } from "shared/datamodel/integration-item";
import { mapMondayColumnValue } from "shared/integrations/mapper";
import { TasksColors } from "elements/gantt/constants";
import { GanttConnector } from "shared/datamodel/schemas/gantt";
import { getMetadataMappingForGantt } from "frontend/canvas-designer-new/integrations/convert-to-monday/logic/gantt-mapping-hooks/use-mapping-settings-gantt-cards";

async function ganttChartMapToElements(
  response: ModelBoardGanttChartResponse
): Promise<{ elements: NewElementData[]; metadata?: { [id: string]: any } }> {
  const { boardIntegrationId: integrationId, boardId } = response.boardContext;
  if (!integrationId) {
    throw new Error("Integration ID is required");
  }

  const elements: TemplateData = {};
  const nodeIdMap = new Map<string, string>(); // AI node ID -> canvas element ID

  let ganttStartDate = Number.MAX_SAFE_INTEGER;
  let ganttEndDate = 0;

  const dateColumn = response.columns.find((c) => c.id === response.chosenColumnIds.dateColumnId);
  const groupByColumn = response.columns.find((c) => c.id === response.chosenColumnIds.groupByColumnId);

  const ganttRows: { id: string; title: string; color: string }[] = [];

  if (!dateColumn || !groupByColumn) {
    throw new Error("Date column or group by column not found");
  }

  const statusColumn = {
    ...groupByColumn,
    settings: JSON.parse(groupByColumn.settings_str),
  };

  const { id, item: ganttItem } = placeGanttElement({ x: 0, y: 0 }, ganttStartDate, ganttEndDate);
  const ganttElementId = `${consts.CANVAS_ELEMENTS.GANTT}-${id}`;

  for (const task of response.tasks) {
    const startDate = getStartDate(task, dateColumn);
    const endDate = getEndDate(task, dateColumn);
    if (!startDate || !endDate) {
      continue;
    }
    if (startDate < ganttStartDate) {
      ganttStartDate = startDate;
    }
    if (endDate > ganttEndDate) {
      ganttEndDate = endDate;
    }
    const integrationItem = placeIntegrationElement({ x: 0, y: 0 }, integrationId, IntegrationType.monday, {
      itemId: task.id,
      groupId: task.groupByColumns["group"],
      title: task.title,
    });

    // handle group by column

    const groupByValue = mapMondayColumnValue({ value: task.groupByColumns[groupByColumn.id] }, groupByColumn.type, {
      column: statusColumn,
    });
    const index = groupByValue ? groupByValue.index.toString() : "5";
    let row = ganttRows.find((row) => row.id === index);
    if (!row) {
      row = {
        id: index,
        title: groupByValue?.text ?? " ",
        color: TasksColors[ganttRows.length % TasksColors.length],
      };
      ganttRows.push(row);
    }

    integrationItem.item.fieldValues = {
      groupByColumn: index,
      fromDate: startDate,
      toDate: endDate,
      title: task.title,
    };
    integrationItem.item.containerId = ganttElementId;
    integrationItem.item.fill = row.color;
    const elementId = `${consts.CANVAS_ELEMENTS.INTEGRATION}-${integrationItem.id}`;
    elements[elementId] = integrationItem.item;
    nodeIdMap.set(task.id, elementId);
  }

  const connectors: GanttConnector[] = response.tasks.reduce((acc, task) => {
    const toId = nodeIdMap.get(task.id);
    const dependencies = task.dependsOn[response.chosenColumnIds.dependencyColumnId];
    if (!dependencies) {
      return acc;
    }
    for (const dependsOnId of dependencies) {
      const fromId = nodeIdMap.get(dependsOnId);
      if (!fromId || !toId) {
        continue;
      }
      acc.push({
        id: `${task.id}-${dependsOnId}`,
        type: "monday",
        from: fromId,
        to: toId,
      });
    }
    return acc;
  }, [] as GanttConnector[]);

  const daysDiff = (ganttEndDate - ganttStartDate) / (1000 * 60 * 60 * 24);
  if (daysDiff > 180) {
    ganttItem.granularity = "quarter";
  } else if (daysDiff > 28) {
    ganttItem.granularity = "month";
  } else if (daysDiff > 21) {
    ganttItem.granularity = "week";
  } else {
    ganttItem.granularity = "day";
  }
  ganttItem.startDate = ganttStartDate;
  ganttItem.endDate = ganttEndDate;
  ganttItem.connectors = connectors;
  ganttItem.splits = [
    {
      id: "groupByColumn",
      type: "custom",
      rows: ganttRows,
    },
  ];
  elements[ganttElementId] = ganttItem;

  const mapping = getMetadataMappingForGantt({
    timeline: response.chosenColumnIds.dateColumnId,
    dependency: response.chosenColumnIds.dependencyColumnId,
  });
  const metadata: { [id: string]: any } = {
    [boardId]: {
      type: "boardColumnsMapping",
      lastModifiedTimestamp: Date.now(),
      boardColumnsMapping: mapping,
    },
    [`IntgMapping-${boardId}`]: {
      ...mapping,
    },
  };

  const result = new Map(Object.entries(centerElementsAroundOrigin(elements)));
  return { elements: result as unknown as NewElementData[], metadata };
}

function getStartDate(task: ModelBoardGanttChartResponse["tasks"][number], chosenColumn: { type: string; id: string }) {
  if (!task.dateColumnValues[chosenColumn.id]) {
    return null;
  }
  const value = JSON.parse(task.dateColumnValues[chosenColumn.id]);
  if (chosenColumn.type === "date") {
    const date = new Date(value.date);
    return date.getTime();
  }
  if (chosenColumn.type === "timeline") {
    return new Date(value.from).getTime();
  }
  return Date.now();
}

function getEndDate(task: ModelBoardGanttChartResponse["tasks"][number], chosenColumn: { type: string; id: string }) {
  if (!task.dateColumnValues[chosenColumn.id]) {
    return null;
  }
  const value = JSON.parse(task.dateColumnValues[chosenColumn.id]);
  if (chosenColumn.type === "date") {
    const date = new Date(value.date);
    return date.getTime();
  }
  if (chosenColumn.type === "timeline") {
    return new Date(value.to).getTime();
  }
  return Date.now();
}

export const boardGanttChartBlock: AIBlockConfig = {
  name: "board-gantt-chart",
  blockType: "generate",
  responseToElements: ganttChartMapToElements,
  defaultPosition: { x: 200, y: 200 },
  shouldCreateIntegration: true,
};
